import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { scansApi } from '../services/api';
import { generateReportPDF } from '../utils/pdfGenerator';
import './Report.css';

interface Issue {
    id: number;
    type: string;
    severity: 'high' | 'medium' | 'low';
    confidence: number;
    description: string;
    evidence_path: string;
}

interface Screenshot {
    id: number;
    path: string;
}

interface Scan {
    id: number;
    session_id: number;
    url: string;
    device: string;
    status: string;
    started_at: string;
    completed_at: string;
}

interface ReportData {
    scan: Scan;
    issues: Issue[];
    screenshots: Screenshot[];
    summary: {
        executive: string;
        issueCount: { high: number; medium: number; low: number };
        totalIssues: number;
    };
}

interface ConsoleLog {
    type: 'log' | 'warn' | 'error' | 'info' | 'debug';
    text: string;
    timestamp: number;
}

interface Cookie {
    name: string;
    value: string;
    domain: string;
    httpOnly: boolean;
    secure: boolean;
}

interface PerformanceMetrics {
    domContentLoaded: number | null;
    loadComplete: number | null;
    firstPaint: number | null;
    firstContentfulPaint: number | null;
    domNodes: number;
}

interface DebugData {
    consoleLogs: ConsoleLog[];
    cookies: Cookie[];
    localStorage: Record<string, string>;
    sessionStorage: Record<string, string>;
    performanceMetrics: PerformanceMetrics;
}

export default function Report() {
    const { scanId } = useParams<{ scanId: string }>();
    const navigate = useNavigate();
    const [report, setReport] = useState<ReportData | null>(null);
    const [debugData, setDebugData] = useState<DebugData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState<'summary' | 'findings' | 'technical'>('summary');
    const [rescanning, setRescanning] = useState(false);
    const [consoleFilter, setConsoleFilter] = useState<'all' | 'error' | 'warn' | 'log'>('all');

    useEffect(() => {
        if (!scanId) return;

        const fetchReport = async () => {
            try {
                const data = await scansApi.getReport(parseInt(scanId));
                setReport(data);
                setLoading(false);

                // Fetch debug data separately (non-blocking)
                try {
                    const debug = await scansApi.getDebugData(parseInt(scanId));
                    setDebugData(debug);
                } catch {
                    // Debug data may not exist for older scans
                }
            } catch (err) {
                setError('Failed to load report');
                setLoading(false);
            }
        };

        // Poll if scan is still running
        const pollInterval = setInterval(async () => {
            if (!scanId) return;
            const data = await scansApi.get(parseInt(scanId));
            if (data.status === 'completed' || data.status === 'failed') {
                clearInterval(pollInterval);
                fetchReport();
            }
        }, 2000);

        fetchReport();

        return () => clearInterval(pollInterval);
    }, [scanId]);

    const handleRescan = async () => {
        if (!report) return;
        setRescanning(true);
        try {
            const result = await scansApi.create({
                url: report.scan.url,
                device: report.scan.device,
                location: 'auto',
            });
            navigate(`/loading/${result.scanId}`);
        } catch (err) {
            setError('Failed to start new scan');
            setRescanning(false);
        }
    };

    const downloadSnapshot = async () => {
        if (!report) return;
        await generateReportPDF(report, debugData);
    };

    if (loading) {
        return (
            <div className="report-loading">
                <div className="loading-content">
                    <div className="spinner-lg"></div>
                    <h2>Analyzing page...</h2>
                    <p className="text-secondary">Capturing evidence and detecting issues</p>
                </div>
            </div>
        );
    }

    if (error || !report) {
        return (
            <div className="report-error">
                <h2>Error Loading Report</h2>
                <p>{error || 'Report not found'}</p>
                <Link to="/" className="btn btn-primary">
                    Start New Scan
                </Link>
            </div>
        );
    }

    const { scan, issues, screenshots, summary } = report;
    const url = new URL(scan.url);

    return (
        <div className="report">
            {/* Header */}
            <header className="report-header">
                <div className="header-left">
                    <div className="scan-info">
                        <h1>{url.hostname}</h1>
                        <span className="url-path">{scan.url}</span>
                    </div>
                </div>
                <div className="header-right">
                    <button
                        className="btn btn-secondary"
                        onClick={downloadSnapshot}
                    >
                        Download Report
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleRescan}
                        disabled={rescanning}
                    >
                        {rescanning ? 'Starting...' : 'Re-scan'}
                    </button>
                </div>
            </header>

            {/* At-a-Glance Summary */}
            <section className="summary-cards">
                <div className={`summary-card ${summary.issueCount.high > 0 ? 'has-issues' : ''}`}>
                    <span className="count high">{summary.issueCount.high}</span>
                    <span className="label">High</span>
                </div>
                <div className={`summary-card ${summary.issueCount.medium > 0 ? 'has-issues' : ''}`}>
                    <span className="count medium">{summary.issueCount.medium}</span>
                    <span className="label">Medium</span>
                </div>
                <div className={`summary-card ${summary.issueCount.low > 0 ? 'has-issues' : ''}`}>
                    <span className="count low">{summary.issueCount.low}</span>
                    <span className="label">Low</span>
                </div>
            </section>

            {/* Executive Summary */}
            <section className="executive-summary card">
                <h2>Executive Summary</h2>
                <p>{summary.executive}</p>
            </section>

            {/* Tabs */}
            <div className="tabs">
                <button
                    className={`tab ${activeTab === 'summary' ? 'active' : ''}`}
                    onClick={() => setActiveTab('summary')}
                >
                    Evidence
                </button>
                <button
                    className={`tab ${activeTab === 'findings' ? 'active' : ''}`}
                    onClick={() => setActiveTab('findings')}
                >
                    Findings ({issues.length})
                </button>
                <button
                    className={`tab ${activeTab === 'technical' ? 'active' : ''}`}
                    onClick={() => setActiveTab('technical')}
                >
                    Technical Detail
                </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
                {activeTab === 'summary' && (
                    <div className="evidence-section">
                        <h3>Visual Evidence</h3>
                        <div className="screenshot-grid">
                            {screenshots.map((screenshot) => (
                                <div key={screenshot.id} className="screenshot-card">
                                    <img
                                        src={`http://localhost:3001${screenshot.path}`}
                                        alt="Page screenshot"
                                        loading="lazy"
                                    />
                                </div>
                            ))}
                            {screenshots.length === 0 && (
                                <p className="no-screenshots text-muted">No screenshots captured</p>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'findings' && (
                    <div className="findings-section">
                        {issues.length === 0 ? (
                            <div className="no-issues">
                                <span className="success-icon">✓</span>
                                <p>No significant issues detected</p>
                            </div>
                        ) : (
                            <div className="issues-list">
                                {issues.map((issue) => (
                                    <div key={issue.id} className={`issue-card severity-${issue.severity}`}>
                                        <div className="issue-header">
                                            <span className={`badge badge-${issue.severity}`}>
                                                {issue.severity}
                                            </span>
                                            <span className="issue-type">{formatIssueType(issue.type)}</span>
                                            <span className="confidence">{issue.confidence}% confidence</span>
                                        </div>
                                        <p className="issue-description">{issue.description}</p>
                                        {issue.evidence_path && (
                                            <details className="issue-evidence">
                                                <summary>View Evidence</summary>
                                                <pre>{issue.evidence_path}</pre>
                                            </details>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'technical' && (
                    <div className="technical-section">
                        <div className="technical-card">
                            <h4>Scan Details</h4>
                            <dl className="details-list">
                                <dt>Started</dt>
                                <dd>{new Date(scan.started_at).toLocaleString()}</dd>
                                <dt>Completed</dt>
                                <dd>{scan.completed_at ? new Date(scan.completed_at).toLocaleString() : 'N/A'}</dd>
                                <dt>Device</dt>
                                <dd>{scan.device}</dd>
                                <dt>Session ID</dt>
                                <dd>{scan.session_id}</dd>
                            </dl>
                        </div>

                        {/* Performance Metrics */}
                        {debugData?.performanceMetrics && (
                            <div className="technical-card">
                                <h4>Performance Metrics</h4>
                                <dl className="details-list">
                                    <dt>DOM Content Loaded</dt>
                                    <dd>{debugData.performanceMetrics.domContentLoaded ? `${debugData.performanceMetrics.domContentLoaded}ms` : 'N/A'}</dd>
                                    <dt>Load Complete</dt>
                                    <dd>{debugData.performanceMetrics.loadComplete ? `${debugData.performanceMetrics.loadComplete}ms` : 'N/A'}</dd>
                                    <dt>First Paint</dt>
                                    <dd>{debugData.performanceMetrics.firstPaint ? `${Math.round(debugData.performanceMetrics.firstPaint)}ms` : 'N/A'}</dd>
                                    <dt>First Contentful Paint</dt>
                                    <dd>{debugData.performanceMetrics.firstContentfulPaint ? `${Math.round(debugData.performanceMetrics.firstContentfulPaint)}ms` : 'N/A'}</dd>
                                    <dt>DOM Nodes</dt>
                                    <dd>{debugData.performanceMetrics.domNodes}</dd>
                                </dl>
                            </div>
                        )}

                        {/* Console Logs */}
                        {debugData?.consoleLogs && debugData.consoleLogs.length > 0 && (
                            <details className="technical-card collapsible">
                                <summary>
                                    <h4>Console Logs ({debugData.consoleLogs.length})</h4>
                                </summary>
                                <div className="console-filter">
                                    <button className={consoleFilter === 'all' ? 'active' : ''} onClick={() => setConsoleFilter('all')}>All</button>
                                    <button className={consoleFilter === 'error' ? 'active' : ''} onClick={() => setConsoleFilter('error')}>Errors</button>
                                    <button className={consoleFilter === 'warn' ? 'active' : ''} onClick={() => setConsoleFilter('warn')}>Warnings</button>
                                    <button className={consoleFilter === 'log' ? 'active' : ''} onClick={() => setConsoleFilter('log')}>Logs</button>
                                </div>
                                <div className="console-logs">
                                    {debugData.consoleLogs
                                        .filter(log => consoleFilter === 'all' || log.type === consoleFilter)
                                        .slice(0, 50)
                                        .map((log, idx) => (
                                            <div key={idx} className={`console-entry console-${log.type}`}>
                                                <span className="console-type">[{log.type}]</span>
                                                <span className="console-time">{log.timestamp}ms</span>
                                                <span className="console-text">{log.text}</span>
                                            </div>
                                        ))}
                                </div>
                            </details>
                        )}

                        {/* Cookies */}
                        {debugData?.cookies && debugData.cookies.length > 0 && (
                            <details className="technical-card collapsible">
                                <summary>
                                    <h4>Cookies ({debugData.cookies.length})</h4>
                                </summary>
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Domain</th>
                                            <th>Secure</th>
                                            <th>HttpOnly</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {debugData.cookies.map((cookie, idx) => (
                                            <tr key={idx}>
                                                <td>{cookie.name}</td>
                                                <td>{cookie.domain}</td>
                                                <td>{cookie.secure ? '✓' : '✗'}</td>
                                                <td>{cookie.httpOnly ? '✓' : '✗'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </details>
                        )}

                        {/* LocalStorage */}
                        {debugData?.localStorage && Object.keys(debugData.localStorage).length > 0 && (
                            <details className="technical-card collapsible">
                                <summary>
                                    <h4>LocalStorage ({Object.keys(debugData.localStorage).length} items)</h4>
                                </summary>
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Key</th>
                                            <th>Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(debugData.localStorage).map(([key, value], idx) => (
                                            <tr key={idx}>
                                                <td>{key}</td>
                                                <td className="value-cell">{value.slice(0, 100)}{value.length > 100 ? '...' : ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </details>
                        )}

                        {/* SessionStorage */}
                        {debugData?.sessionStorage && Object.keys(debugData.sessionStorage).length > 0 && (
                            <details className="technical-card collapsible">
                                <summary>
                                    <h4>SessionStorage ({Object.keys(debugData.sessionStorage).length} items)</h4>
                                </summary>
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Key</th>
                                            <th>Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(debugData.sessionStorage).map(([key, value], idx) => (
                                            <tr key={idx}>
                                                <td>{key}</td>
                                                <td className="value-cell">{value.slice(0, 100)}{value.length > 100 ? '...' : ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </details>
                        )}

                        <div className="technical-card">
                            <h4>Downloads</h4>
                            <div className="download-buttons">
                                <a
                                    href={`http://localhost:3001/storage/har/${scanId}/network.har`}
                                    download={`scan-${scanId}-network.har`}
                                    className="btn btn-secondary"
                                >
                                    Download HAR
                                </a>
                                <a
                                    href={`http://localhost:3001/storage/har/${scanId}/trace.json`}
                                    download={`scan-${scanId}-trace.json`}
                                    className="btn btn-secondary"
                                >
                                    Download Trace
                                </a>
                                <a
                                    href={`http://localhost:3001/storage/har/${scanId}/debug-data.json`}
                                    download={`scan-${scanId}-debug-data.json`}
                                    className="btn btn-secondary"
                                >
                                    Download Debug Data
                                </a>
                            </div>
                            <p className="text-muted text-sm mt-sm">
                                HAR = network requests, Trace = timeline &amp; diagnostics, Debug Data = console logs, cookies, storage
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function formatIssueType(type: string): string {
    return type
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}
